import Big, { RoundingMode } from 'big.js';
import { NumberHelperService } from './number-helper.service';
import { BillingRate } from '../models';

export const DefaultPayRateMultiplier = 1.5;
export const AverageWeeksPerMonth = 4.33; // 52 weeks / 12 months

export class FormulaHelperService {
    static roundNumber(value: number, roundToNextWholeNumber: boolean = false): number {
        if (isNaN(value)) {
            return 0;
        }
        if (roundToNextWholeNumber) {
            return Math.round(value);
        }
        return new Big(value).round(2, Big.roundHalfUp).toNumber();
    }

    /**
     * Calculates the minimum premium pay rate
     *
     * @param basePayRate base pay rate
     * @returns number rounded to the cent using the round half up rounding method.
     */
    static calculateMinPremiumPayRate(basePayRate: number): number {
        return new Big(basePayRate).times(DefaultPayRateMultiplier).round(2, Big.roundHalfUp).toNumber();
    }

    static calculateShiftRate(otPayRate: number, blendedPayRate: number, shiftLength: number): number {
        const length = Number(shiftLength);

        if (length === 8) {
            return FormulaHelperService.roundNumber(
                (otPayRate - FormulaHelperService.roundNumber(blendedPayRate * DefaultPayRateMultiplier)) * length
            );
        } else {
            return FormulaHelperService.roundNumber(
                FormulaHelperService.roundNumber(otPayRate - blendedPayRate) * length
            );
        }
    }

    static calculateShift(otPayRate: number, blendedPayRate: number, shiftLength: number): number {
        return FormulaHelperService.roundNumber(
            FormulaHelperService.calculateShiftRate(
                NumberHelperService.formatNumber(otPayRate),
                blendedPayRate,
                NumberHelperService.formatNumber(shiftLength)
            ),
            true
        );
    }

    static calculateAdjustedRate(
        rate: number,
        contractFees: number,
        extFees: number,
        earlyPmtDiscount: number
    ): number {
        return new Big(rate)
            .times(1 - contractFees)
            .times(1 - extFees)
            .times(1 - earlyPmtDiscount)
            .round(2, Big.roundHalfUp)
            .toNumber();
    }

    static calculateFacilityBillingMargin(payRate: number, multiplier: number, adjustedBillRate: number): number {
        if (adjustedBillRate === 0) {
            return null;
        }
        return (adjustedBillRate - payRate * (1 + multiplier)) / adjustedBillRate;
    }

    static calculateOnCallBillRate(
        multiplier: string,
        multiplierAmount: number,
        baseRate: number,
        billingRate: BillingRate
    ): number {
        if (!billingRate || !billingRate.includeOC) {
            return 0;
        } else if (multiplier === ' ' || !multiplierAmount) {
            return -1;
        } else {
            switch (multiplier) {
                case '%':
                    return baseRate * multiplierAmount;
                case '$':
                    return multiplierAmount;
                default:
                    return 0;
            }
        }
    }

    static addOtRate(multiplierAmount: number, baseBillRate: number, rm?: RoundingMode): number {
        return new Big(baseBillRate).add(multiplierAmount).round(2, rm).toNumber();
    }

    static multiplyOtRate(multiplierAmount: number, baseBillRate: number, rm?: RoundingMode): number {
        return new Big(baseBillRate).times(multiplierAmount).round(2, rm).toNumber();
    }

    static calculateRateByMultiplier(
        multiplier: string,
        multiplierAmount: number,
        baseRate: number,
        rm?: RoundingMode
    ): number {
        let rate = 0;
        if (multiplierAmount === null) {
            return baseRate;
        }
        switch (multiplier) {
            case '%':
                rate = this.multiplyOtRate(multiplierAmount, baseRate, rm);
                break;
            case '$':
                rate = this.addOtRate(multiplierAmount, baseRate, rm);
                break;
            default:
                rate = baseRate;
                break;
        }

        return rate;
    }

    static adjustOrientRate(orientRate: number): number {
        return Big(orientRate).div(100).toNumber();
    }

    static calculateAdjustedOrientationRate(
        extension: boolean,
        orientHours: number,
        orientRate: number,
        paybackPercent: number,
        extDiscount: number,
        discountPercent: number,
        extDiscountType: boolean
    ): number {
        if (extension && extDiscountType) {
            extDiscount *= 100;
            return Big(orientHours)
                .times(
                    Big(orientRate)
                        .times(1 - paybackPercent)
                        .times(1 - discountPercent)
                        .minus(extDiscount)
                )
                .toNumber();
        }
        return Big(orientHours)
            .times(
                Big(orientRate)
                    .times(1 - paybackPercent)
                    .times(1 - extDiscount)
                    .times(1 - discountPercent)
            )
            .toNumber();
    }

    static calculateBillingHour(
        bookingWeeks: number,
        shiftsPerWeek: number,
        shiftLength: number,
        otHours: number
    ): number {
        return new Big(Big(shiftLength).times(shiftsPerWeek).times(bookingWeeks)).minus(otHours).toNumber();
    }
}
